/**
 * Wire shapes for the Documentation section.
 *
 * These mirror portal-api's Alba serializers (`DocumentSerializer`,
 * `DocumentDetailSerializer`, `DocumentCategorySerializer`,
 * `SolutionAreaSerializer`) as the reseller BFF relays them — which is the
 * same JSON the command center reads, because it is the same serializers.
 *
 * WHY A LOCAL COPY rather than importing the command center's
 * `@xop/dashboard-shared`. That package is in another repo with its own
 * deploy cadence, and this portal already made the same call for the CRM
 * port (`src/crm/types`). What matters is that the shapes agree; a shared
 * package would only add a release train between two apps that talk to the
 * same Rails serializer anyway.
 *
 * WHAT IS DELIBERATELY ABSENT: every authoring shape. There are no create /
 * update / AI-generate payloads here, because the reseller portal cannot
 * write to the documentation — portal-api's `Api::V1::Reseller::*`
 * namespace exposes no write to call. If you find yourself adding one, the
 * question to answer first is why a partner is editing our library.
 *
 * Any field portal-api may omit is typed `| null` (nulls are ordinary in
 * ActiveRecord JSON) so pages branch on null rather than leaning on
 * `undefined`.
 */

/** A reseller only ever sees `published` — the other two exist in the type
 * because the serializer emits the column, not because they can arrive. */
export type DocStatus = "draft" | "in_review" | "published";

/** Selects the renderer: `article`/`guide`/`slide` render markdown, `video`
 * opens the player, `faq` the accordion, `file` the attachment preview.
 * Mirrors the Rails `documents.doc_type` allow-list. */
export type DocType = "article" | "video" | "guide" | "faq" | "slide" | "file";

/** What a document is FOR — mirrors portal-api's `Document::PURPOSES`. */
export type DocPurpose = "documentation" | "training";

/** A go-to-market pillar as it rides on a document row (many-to-many). */
export interface DocSolutionAreaTag {
  id: number;
  slug: string;
  name: string;
}

/** A pillar as the managed taxonomy lists it. `visible_in_reseller_portal`
 * is the eye toggle staff flip in the command center — portal-api already
 * filters on it, so this is only ever informational here. */
export interface DocSolutionAreaRef {
  id: number;
  name: string;
  slug: string;
  position?: number | null;
  visible_in_customer_portal?: boolean;
  visible_in_reseller_portal?: boolean;
  document_count?: number;
}

export interface DocFaqItem {
  id: number;
  anchor: string;
  question: string;
  answer_markdown: string;
}

export interface DocVideoPayload {
  kind: "upload" | "youtube" | "vimeo" | "embed";
  /** Iframe-safe embed URL (YouTube/Vimeo player, Google Drive `/preview`,
   * or a trusted arbitrary embed). `null` for uploads + direct links. */
  provider_url: string | null;
  /** Direct video-FILE URL for the native `<video>` player — set when the
   * authored link is raw media rather than an embeddable page. Mutually
   * exclusive with `provider_url`. */
  direct_url: string | null;
  thumbnail: string | null;
  duration: number | null;
  captions_url: string | null;
  chapters: Array<{ time: number; title: string }>;
  file: DocAttachment | null;
}

export interface DocCategory {
  id: number;
  name: string;
  slug: string;
  icon: string | null;
  description?: string | null;
  position?: number | null;
  parent_id?: number | null;
  visible_in_customer_portal?: boolean;
  /** The folder tick staff set. `false` only on a CONTAINER — a folder
   * portal-api returns solely so an allowed sub-folder keeps its place; it
   * holds nothing this company can read and carries no description. */
  visible_in_reseller_portal?: boolean;
  /** This folder's OWN readable articles. */
  document_count?: number;
  /** Its own plus every sub-folder's, counted from what THIS company can
   * read (portal-api `Documents::FolderList`, 2026-10-31). Absent from an
   * older portal-api — `folderCount` in `folder-tree.ts` then counts. */
  total_count?: number;
  children?: {
    id: number;
    name: string;
    slug: string;
    icon: string | null;
    document_count?: number;
    total_count?: number;
  }[];
}

export interface DocRelease {
  id: number;
  version: string;
  name?: string | null;
}

export interface DocAuthor {
  id: number;
  name: string;
  email?: string | null;
  avatar_url?: string | null;
}

export interface DocAttachment {
  id: number;
  filename: string;
  byte_size: number;
  content_type: string | null;
  /** ActiveStorage proxy URL with `disposition=attachment`. Note the reader
   * never fetches these directly — they are cross-origin, which taints the
   * canvas the PDF viewer draws onto. The BFF's `/documents/:id/file` relay
   * is the path the browser actually uses. */
  download_url: string | null;
  /** Proxy URL with `disposition=inline`. */
  preview_url: string | null;
  created_at: string;
}

export interface DocSummary {
  id: number;
  title: string;
  slug: string;
  summary: string | null;
  status: DocStatus;
  doc_type: DocType;
  solution_areas: DocSolutionAreaTag[];
  /** Transitional singular the serializer still emits for staggered-deploy
   * safety. Read `solution_areas`. */
  solution_area?: string | null;
  featured: boolean;
  pinned: boolean;
  views_count: number;
  likes_count: number;
  average_rating: number | null;
  ratings_count: number;
  versions_count: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  /** The document's OWN date, detected from an uploaded PDF's embedded
   * /CreationDate — distinct from `created_at`, which is upload time.
   * `null` when nothing was detected; the list shows "—" and sorts last. */
  document_date: string | null;
  thumbnail_url: string | null;
  external_url: string | null;
  metadata: Record<string, unknown> | null;
  author: DocAuthor | null;
  category: { id: number; name: string; slug: string; icon: string | null } | null;
  default_release: { id: number; version: string } | null;
  /** Which SHELF the document sits on (2026-09-13): `release` when it is
   * pinned to a release version and read under that release, `library`
   * otherwise — the library IS the documentation. portal-api stamps it on
   * every row; an older serializer omits it, and then `default_release`
   * decides (`isLibraryDoc` in `folder-filters.ts`). */
  shelf?: "release" | "library";
  /** WHAT the document is for (2026-09-17): `documentation` (the default —
   * library articles and release documentation) or `training` (a PDF or
   * video staff attached to a release as TRAINING). portal-api never lists a
   * training with documentation; the Trainings page reads them from their
   * own endpoint. Optional because an older portal-api omits it. */
  purpose?: DocPurpose;
  /** A training's RELEASE (10.2) when its `default_release` is a version
   * under one (10.2.0.27) — how the Trainings page groups. Emitted by
   * `GET /api/v1/reseller/trainings`; absent elsewhere, and absent when the
   * document is pinned to the release itself. */
  release_line?: { id: number; version: string } | null;
  current_version: { id: number; version_number: string; status: string } | null;
  tags: { id: number; name: string; color: string | null }[];
}

export interface DocDetail extends DocSummary {
  /** Markdown body of the current version. Null for an upload-mode document
   * (the file IS the content). */
  content: string | null;
  files: DocAttachment[];
  /** The faithful ORIGINAL a markdown body was transcribed from — offered as
   * "Download original". Null for hand-authored docs and plain uploads. */
  source_file?: DocAttachment | null;
  video?: DocVideoPayload | null;
  /** Parsed FAQ items when `doc_type === "faq"`. `null` means "no parseable
   * structure" and the reader falls back to plain markdown. */
  faq_items?: DocFaqItem[] | null;
}

export interface PaginatedMeta {
  page: number;
  per_page: number;
  total_pages: number;
  total_count: number;
}

export interface PaginatedDocList {
  data: DocSummary[];
  meta: PaginatedMeta;
}

export interface SingleDoc {
  data: DocDetail;
}

export interface CategoryListResponse {
  data: DocCategory[];
}

export interface SolutionAreaListResponse {
  data: DocSolutionAreaRef[];
}

/**
 * The mat colour behind a rendered PDF page. Copied from the command
 * center's `PDF_VIEWER_MAT_BG` so the inline preview reads the same in both
 * apps — a deliberate constant rather than a theme token, because it is the
 * neutral grey a white sheet has to sit on in BOTH light and dark mode.
 */
export const PDF_VIEWER_MAT_BG = "#3a3d42";
